import { ChangeEvent, useCallback, useState } from "react";
import { useAPI } from "../../apiv1";
import { Error as APIError } from "../../types/apiv1";
import { useAppSelector } from "../../store/hooks";
import InviteUsersUI, { SubmitError, Fields, Row, ImportError } from "../ui/InviteUsersUI";
import parseInviteUsersSpreadsheet from "../../parseInviteUsersSpreadsheet";

const uniqueID = (() => {
	let id = 0;
	return () => id++;
})();

export default function InviteUsers() {
	const api = useAPI();
	const sessionState = useAppSelector(state => state.session);
	const [pending, setPending] = useState(false);
	const [rows, setRows] = useState<Row[]>([]);
	const [importError, setImportError] = useState<ImportError | null>(null);

	const addRow = useCallback(() => {
		setRows(rows => [
			...rows,
			{
				id: uniqueID(),
				fields: {
					givenName: "",
					familyName: "",
					dob: "",
					email: "",
				},
				error: null,
			},
		]);
	}, [setRows]);

	const removeRow = useCallback((rowID: number) => {
		setRows(rows => {
			const index = rows.findIndex(row => row.id === rowID);
			if (index === -1) {
				console.warn("attempted to remove non-exisxtant row");
				return rows;
			};
			let newRows = [...rows];
			newRows.splice(index, 1);
			return newRows;
		});
	}, [setRows]);

	const setErrorOfRow = useCallback((rowID: number, error: SubmitError) => {
		setRows(rows => {
			const index = rows.findIndex(row => row.id === rowID);
			if (index === -1) {
				console.warn("attempted to remove non-exisxtant row");
				return rows;
			};
			let newRows = [...rows];
			newRows[index] = {
				...rows[index],
				error,
			}
			return newRows;
		});
	}, [setRows]);

	const onFieldChange = useCallback((rowID: number, field: keyof Fields, value: string) => {
		setRows(rows => {
			const index = rows.findIndex(row => row.id === rowID);
			if (index === -1) {
				console.warn("attempted to update non-exisxtant row");
				return rows;
			};
			let newRows = [...rows];
			newRows[index] = {
				...rows[index],
				fields: {
					...rows[index].fields,
					[field]: value,
				},
			};
			return newRows;
		});
	}, [setRows]);

	const onImport = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files || event.target.files.length !== 1) {
			return;
		}
		const file = event.target.files[0];
		const data = await file.arrayBuffer();
		const fieldsOfUsers = parseInviteUsersSpreadsheet(data);
		if (fieldsOfUsers == null) {
			setImportError(ImportError.InvalidSheet);
			return;
		}
		if (fieldsOfUsers.length === 0) {
			setImportError(ImportError.EmptySheet);
			return;
		}
		const rows = fieldsOfUsers.map(fields => ({
			id: uniqueID(),
			fields: fields,
			error: null,
		}));
		setImportError(null);
		setRows(rows);
	}, [setImportError, setRows]);

	const onSubmit = useCallback(async () => {
		if (!sessionState.authenticated)
			return;

		setPending(true);
		await Promise.all(rows.map(async row => {
			const { email, givenName, familyName } = row.fields;

			try {
				if (email !== "") {
					await api.organizations.id(sessionState.activeOrganization)
						.invites.post(
							email, givenName, "", familyName);
				}
				removeRow(row.id);
			} catch (e) {
				switch (e) {
				case APIError.Network:
					setErrorOfRow(row.id, SubmitError.NetworkError);
					break;
				case APIError.Conflict:
					setErrorOfRow(row.id, SubmitError.ConflictError);
					break;
				case APIError.Forbidden:
					setErrorOfRow(row.id, SubmitError.PermissionError);
					break;
				case APIError.BadRequest:
					setErrorOfRow(row.id, SubmitError.InvalidError);
					break;
				default:
					setErrorOfRow(row.id, SubmitError.Bug);
				}
			}
		}));
		setPending(false);
	}, [rows, sessionState, api, removeRow, setPending, setErrorOfRow]);

	return <InviteUsersUI
		pending={pending}
		rows={rows}
		importError={importError}
		onAddRowClick={addRow}
		onRemoveRowClick={removeRow}
		onFieldChange={onFieldChange}
		onImport={onImport}
		onSubmit={(!pending && rows.length > 0) ? onSubmit : undefined}
	/>;
}
